import React from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { sfHeadline, sfSub } from './sectionHelpers';

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  photo_url: string;
  linkedin_url: string;
}

const TeamGrid: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const members = (c.members || []) as TeamMember[];
  const variant = (c.variant as string) || '3_col';
  const cols = variant === '4_col' ? 4 : 3;

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 'var(--brand-card-radius, 12px)',
    padding: 24,
    textAlign: 'center',
  };

  const listItemStyle: React.CSSProperties = {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-start',
    padding: 24,
    background: '#fff',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: 'var(--brand-card-radius, 12px)',
    marginBottom: 16,
  };

  const Avatar: React.FC<{ member: TeamMember; size: number; shape: 'circle' | 'square' }> = ({ member, size, shape }) => {
    const radius = shape === 'circle' ? '50%' : 'var(--brand-card-radius, 12px)';
    if (member.photo_url) {
      return (
        <img
          src={member.photo_url}
          alt={member.name}
          style={{
            width: size,
            height: size,
            borderRadius: radius,
            objectFit: 'cover',
            flexShrink: 0,
            display: 'block',
            ...(shape === 'square' ? { aspectRatio: '1/1', width: '100%', height: 'auto' } : {}),
          }}
        />
      );
    }
    return (
      <div style={{
        width: shape === 'square' ? '100%' : size,
        height: shape === 'square' ? 'auto' : size,
        aspectRatio: shape === 'square' ? '1/1' : undefined,
        borderRadius: radius,
        background: 'var(--brand-primary, #2563eb)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: shape === 'square' ? '2.5rem' : '1.5rem',
        fontWeight: 700,
        flexShrink: 0,
        marginBottom: shape === 'square' ? 16 : 0,
      }}>
        {member.name ? member.name[0].toUpperCase() : '?'}
      </div>
    );
  };

  return (
    <section className="sb-render sb-render-team-grid" style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          {c.section_label && (
            <span style={{
              display: 'block', fontSize: '0.875rem', fontWeight: 600,
              color: 'var(--brand-primary, #2563eb)', textTransform: 'uppercase',
              letterSpacing: '0.05em', marginBottom: 8,
            }}>
              {c.section_label}
            </span>
          )}
          <InlineEditText
            tag="h2"
            controlKey="headline"
            style={{ fontSize: '2.25rem', marginBottom: 12 }}
          >
            {sfHeadline(c, 'Meet the Team')}
          </InlineEditText>
          <InlineEditText
            tag="p"
            controlKey="subheadline"
            style={{ fontSize: '1.125rem', color: '#64748b', maxWidth: 640, margin: '0 auto' }}
          >
            {sfSub(c, '')}
          </InlineEditText>
        </div>

        {variant === 'list' ? (
          <div>
            {members.map((m, i) => (
              <div key={i} style={listItemStyle}>
                <Avatar member={m} size={80} shape="circle" />
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: '0 0 4px' }}>{m.name}</h3>
                  <p style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 500, margin: '0 0 8px' }}>{m.role}</p>
                  <p style={{ color: '#64748b', margin: '0 0 8px', lineHeight: 1.6 }}>{m.bio}</p>
                  {m.linkedin_url && (
                    <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                       style={{ fontSize: '0.875rem', color: 'var(--brand-primary, #2563eb)' }}>
                      LinkedIn
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gap: 24,
          }}>
            {members.map((m, i) => (
              <div key={i} style={cardStyle}>
                <Avatar member={m} size={80} shape="square" />
                <h3 style={{ margin: '0 0 4px' }}>{m.name}</h3>
                <p style={{ color: 'var(--brand-primary, #2563eb)', fontWeight: 500, margin: '0 0 8px' }}>{m.role}</p>
                <p style={{ color: '#64748b', fontSize: '0.9rem', margin: '0 0 12px', lineHeight: 1.6 }}>{m.bio}</p>
                {m.linkedin_url && (
                  <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer"
                     style={{ fontSize: '0.875rem', color: 'var(--brand-primary, #2563eb)' }}>
                    LinkedIn &rarr;
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TeamGrid;
